import { useMemo } from 'react'
import { Icon } from '@iconify/react'
import type { RaspadinhaCampaign } from '@/types/raspadinha-campaign'
import {
  buildCampaignParticipantsCsvFilename,
  collectCampaignParticipants,
  downloadCampaignParticipantsCsv,
  formatParticipantDateTime,
} from '@/lib/raspadinha-campaigns'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import styles from './RaspadinhaGanhadoresSection.module.css'
import participantesStyles from './RaspadinhaParticipantesSection.module.css'

type RaspadinhaParticipantesSectionProps = {
  form: RaspadinhaCampaign
}

function CellText({ value, muted = false }: { value: string; muted?: boolean }) {
  const trimmed = value.trim()
  return (
    <span className={trimmed ? (muted ? styles.cellMuted : styles.cellText) : styles.cellEmpty}>
      {trimmed || '—'}
    </span>
  )
}

export function RaspadinhaParticipantesSection({ form }: RaspadinhaParticipantesSectionProps) {
  const participantRows = useMemo(() => collectCampaignParticipants(form), [form])

  const winnersCount = participantRows.filter((row) => row.role === 'ganhador').length
  const participantsOnlyCount = participantRows.length - winnersCount

  const handleExportParticipantsCsv = () => {
    downloadCampaignParticipantsCsv(form, buildCampaignParticipantsCsvFilename(form.slug))
  }

  return (
    <section className={editStyles.editSection} aria-labelledby="raspadinha-participantes">
      <div className={styles.sectionHeaderWithAction}>
        <h2
          id="raspadinha-participantes"
          className={`${editStyles.editSectionTitle} ${styles.sectionHeaderTitle}`}
        >
          <Icon className={editStyles.editSectionTitleIcon} icon="mdi:account-multiple-outline" />
          Participantes
        </h2>
        <button
          type="button"
          className={styles.exportListButton}
          onClick={handleExportParticipantsCsv}
          disabled={participantRows.length === 0}
          title={
            participantRows.length === 0
              ? 'Não há participantes para exportar'
              : 'Baixar lista de participantes em CSV'
          }
        >
          <Icon icon="mdi:file-export-outline" aria-hidden />
          Exportar CSV
        </button>
      </div>

      <div className={editStyles.editSectionBody}>
        <p className={`${editStyles.editLabelHint} ${styles.summaryHint}`}>
          {participantRows.length} participante(s) no total · {winnersCount} ganhador(es) ·{' '}
          {participantsOnlyCount} participante(s) sem prêmio.
        </p>

        {participantRows.length === 0 ? (
          <p className={styles.emptyState}>Nenhum participante registrado nesta campanha.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Nome</th>
                  <th>CPF / documento</th>
                  <th>E-mail</th>
                  <th>Telefone</th>
                  <th>Data de participação</th>
                  <th>Cartelas</th>
                  <th>Prêmio</th>
                  <th>Dia contemplado</th>
                  <th>Horário contemplado</th>
                </tr>
              </thead>
              <tbody>
                {participantRows.map((row) => (
                  <tr key={row.participant.id}>
                    <td>
                      <span
                        className={
                          row.role === 'ganhador'
                            ? participantesStyles.roleBadgeWinner
                            : participantesStyles.roleBadgeParticipant
                        }
                      >
                        {row.role === 'ganhador' ? 'Ganhador' : 'Participante'}
                      </span>
                    </td>
                    <td>
                      <CellText value={row.participant.name} />
                    </td>
                    <td>
                      <CellText value={row.participant.document} />
                    </td>
                    <td>
                      <CellText value={row.participant.email} />
                    </td>
                    <td>
                      <CellText value={row.participant.phone} />
                    </td>
                    <td>
                      <CellText value={formatParticipantDateTime(row.participant.participated_at)} />
                    </td>
                    <td>
                      <CellText value={String(row.participant.tickets_count)} />
                    </td>
                    <td>
                      <CellText value={row.prizeLabel ?? ''} muted={row.role === 'participante'} />
                    </td>
                    <td>
                      <CellText value={row.wonDateLabel ?? ''} muted={row.role === 'participante'} />
                    </td>
                    <td>
                      <CellText value={row.wonTimeLabel ?? ''} muted={row.role === 'participante'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}
